import { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AnomalyChart,
  ChartChangepointMarker,
  Dialog,
  FlexColumn,
  FormField,
  TextField,
} from '@elementary/common-ui/components';
import { CloudTestFeedbackType } from '@elementary/common-ui/services/gql/types';
import useAiChatPanel from '@elementary/common-ui/hooks/useAiChatPanel';
import { Typography } from '@mui/material';
import useSnackbar from 'shared/hooks/useSnackbar';
import { CreateFeedbackRequest } from 'shared/types';
import {
  CHANGEPOINT_ADDED_TOAST,
  CHANGEPOINT_PROMPT,
  CHANGEPOINT_RERUN_TOAST,
  CONTEXT_FIELD_TITLE,
  CONTEXT_NOTE,
  CONTEXT_PLACEHOLDER,
  CONTEXT_SAVED_TOAST,
  DEFAULT_FEEDBACK_ERROR_MESSAGE,
  EARLIER_ANOMALIES_NOT_COVERED_TOAST,
  EXPECTED_CHANGE_STEP_TITLE,
  FEEDBACK_OPTION_COPY,
  FEEDBACK_OPTION_TYPE,
  OPTIONS_STEP_TITLE,
  REAL_ISSUE_MESSAGE,
  RESULT_FEEDBACK_DIALOG_TITLE,
} from '../constants';
import useCreateFeedback from '../hooks/useCreateFeedback';
import useFeedbackTracking from '../hooks/useFeedbackTracking';
import {
  ExistingChangepoint,
  FeedbackEntryPoint,
  FeedbackOption,
  FeedbackResult,
  FeedbackStep,
  FeedbackTest,
  FeedbackTrackingProperties,
} from '../types';
import {
  formatSelectedPoint,
  getAnotherChangepointMessage,
  getBucketMetrics,
  getFeedbackErrorResponse,
  getReduceSensitivityMessage,
  getReplaceChangepointMessage,
  isSameBucketStart,
} from '../utils';
import FeedbackOptionCard from './FeedbackOptionCard';

const OPTIONS_STEP_OPTIONS: FeedbackOption[] = [
  'expected_change',
  'real_issue',
  'add_context',
];
const EXPECTED_CHANGE_STEP_OPTIONS: FeedbackOption[] = [
  'add_changepoint',
  'reduce_sensitivity',
];

type Props = {
  onClose: () => void;
  test: FeedbackTest;
  result: FeedbackResult;
  availableTypes: CloudTestFeedbackType[];
  entryPoint: FeedbackEntryPoint;
  defaultBucketStart?: string;
  changepointMarkers?: ChartChangepointMarker[];
  existingChangepoints?: ExistingChangepoint[];
};

const ResultFeedbackDialog = ({
  onClose,
  test,
  result,
  availableTypes,
  entryPoint,
  defaultBucketStart,
  changepointMarkers,
  existingChangepoints,
}: Props) => {
  const [step, setStep] = useState<FeedbackStep>('options');
  const [comment, setComment] = useState('');
  const [isConfirmingAnother, setIsConfirmingAnother] = useState(false);
  const metrics = useMemo(
    () => getBucketMetrics(result.chart.metrics),
    [result.chart.metrics],
  );
  // The first point can't be picked, so the default is the first failure after it.
  const defaultPoint = useMemo(
    () =>
      metrics.find((metric) => metric.bucket_start === defaultBucketStart) ??
      metrics.slice(1).find((metric) => metric.is_anomalous) ??
      metrics[metrics.length - 1],
    [metrics, defaultBucketStart],
  );
  const [pickedBucketStart, setPickedBucketStart] = useState(
    defaultPoint?.bucket_start,
  );
  const pickedPoint = metrics.find(
    (metric) => metric.bucket_start === pickedBucketStart,
  );

  const { openSnackbar } = useSnackbar();
  const { openPanel, hasAiPanelAccess } = useAiChatPanel();
  const trackingProperties = useMemo(
    () => ({ result_status: result.status, entry_point: entryPoint }),
    [result.status, entryPoint],
  );
  const track = useFeedbackTracking({
    test,
    resultId: result.id,
    properties: trackingProperties,
  });
  const { mutateAsync: createFeedback } = useCreateFeedback();

  useEffect(() => {
    track('RESULT_FEEDBACK_DIALOG_OPENED');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goToStep = (nextStep: FeedbackStep) => {
    setIsConfirmingAnother(false);
    setStep(nextStep);
  };

  const handleClose = () => {
    track('RESULT_FEEDBACK_DIALOG_CLOSED', { step });
    onClose();
  };

  const isOptionVisible = (option: FeedbackOption): boolean =>
    option === 'expected_change'
      ? EXPECTED_CHANGE_STEP_OPTIONS.some(isOptionVisible)
      : availableTypes.includes(FEEDBACK_OPTION_TYPE[option]) &&
        (option !== 'reduce_sensitivity' || hasAiPanelAccess);

  const handleOptionClick = (option: FeedbackOption) => {
    track('RESULT_FEEDBACK_OPTION_CLICKED', { option });
    if (option === 'reduce_sensitivity') {
      if (defaultPoint) {
        openPanel({
          startMessage: getReduceSensitivityMessage(
            test.tableName ?? test.name,
            defaultPoint,
          ),
          context: { testIds: [test.id], displayLabels: [test.name] },
          origin: 'ai_cta',
        });
      }
      track('RESULT_FEEDBACK_SUBMITTED', {
        option,
        feedback_type: 'TEST_TOO_SENSITIVE',
        ai_chat_opened: !!defaultPoint,
      });
      onClose();
      return;
    }
    goToStep(option);
  };

  const submit = async (
    feedback: CreateFeedbackRequest,
    trackingProperties: FeedbackTrackingProperties,
  ) => {
    onClose();
    try {
      const [created] = await createFeedback(feedback);
      track('RESULT_FEEDBACK_SUBMITTED', {
        ...trackingProperties,
        feedback_type: feedback.feedback_type,
        success: true,
      });
      return created;
    } catch (error) {
      const response = getFeedbackErrorResponse(error);
      track('RESULT_FEEDBACK_SUBMITTED', {
        ...trackingProperties,
        feedback_type: feedback.feedback_type,
        success: false,
        error_code: response?.elementary_error_code ?? null,
      });
      openSnackbar({
        variant: 'error',
        message: response?.error_message ?? DEFAULT_FEEDBACK_ERROR_MESSAGE,
      });
      return undefined;
    }
  };

  const submitContext = async () => {
    if (!defaultPoint) return;
    const created = await submit(
      {
        cloud_test_id: test.id,
        feedback_type: 'CONTEXT',
        cloud_test_result_id: result.id,
        bucket_start: defaultPoint.bucket_start,
        comment: comment.trim(),
      },
      { option: 'add_context' },
    );
    if (created) openSnackbar({ message: CONTEXT_SAVED_TOAST });
  };

  const submitChangepoint = async (overrideExisting = false) => {
    if (!defaultPoint || !pickedPoint) return;
    const created = await submit(
      {
        cloud_test_id: test.id,
        feedback_type: 'EXPECTED_CHANGE_POINT',
        cloud_test_result_id: result.id,
        bucket_start: pickedPoint.bucket_start,
        ...(overrideExisting && { override_existing: true }),
      },
      {
        option: 'add_changepoint',
        picked_is_default_point:
          pickedPoint.bucket_start === defaultPoint.bucket_start,
        override_existing: overrideExisting,
      },
    );
    if (created) {
      const message = [
        created.requires_test_rerun
          ? CHANGEPOINT_RERUN_TOAST
          : CHANGEPOINT_ADDED_TOAST,
        created.other_anomalies_before_bucket.length > 0 &&
          EARLIER_ANOMALIES_NOT_COVERED_TOAST,
      ]
        .filter(Boolean)
        .join(' ');
      openSnackbar({ message });
    }
  };

  const otherChangepoints = pickedPoint
    ? (existingChangepoints ?? []).filter(
        (feedback) =>
          !isSameBucketStart(feedback.bucketStart, pickedPoint.bucket_start),
      )
    : [];
  const pickedPointChangepoint = pickedPoint
    ? existingChangepoints?.find((feedback) =>
        isSameBucketStart(feedback.bucketStart, pickedPoint.bucket_start),
      )
    : undefined;

  const handlePointClick = (bucketStart: string) => {
    setIsConfirmingAnother(false);
    setPickedBucketStart(bucketStart);
  };

  const handleAddChangepointClick = () => {
    if (pickedPointChangepoint) {
      track('RESULT_FEEDBACK_OVERRIDE_CONFIRM_SHOWN', {
        feedback_type: 'EXPECTED_CHANGE_POINT',
        existing_feedback_id: pickedPointChangepoint.id,
      });
      goToStep('override_confirm');
      return;
    }
    if (!otherChangepoints.length) {
      submitChangepoint();
      return;
    }
    track('RESULT_FEEDBACK_ANOTHER_CHANGEPOINT_CONFIRM_SHOWN', {
      existing_feedback_ids: otherChangepoints
        .map((feedback) => feedback.id)
        .join(','),
    });
    setIsConfirmingAnother(true);
  };

  // The runner needs a point before the changepoint, so the first one can't be picked.
  const firstBucketStart = metrics[0]?.bucket_start;
  const disabledBucketStarts = firstBucketStart ? [firstBucketStart] : [];
  const isPickedPointValid =
    !!pickedPoint && pickedPoint.bucket_start !== firstBucketStart;

  const backTo = (previousStep: FeedbackStep) => ({
    hideCancelButton: true,
    actionButtonText: 'Back',
    actionButtonVariant: 'contained' as const,
    actionButtonColor: 'secondary' as const,
    onActionButtonClick: () => goToStep(previousStep),
  });

  const footerProps = (() => {
    switch (step) {
      case 'options':
        return { hideFooter: true };
      case 'real_issue':
      case 'expected_change':
        return { ...backTo('options'), hideConfirmButton: true };
      case 'add_context':
        return {
          ...backTo('options'),
          confirmText: 'Save',
          confirmDisabled: !comment.trim(),
          onConfirmButtonClick: submitContext,
        };
      case 'add_changepoint':
        if (isConfirmingAnother) {
          return {
            cancelText: 'Cancel',
            onCancelButtonClick: () => {
              track('RESULT_FEEDBACK_ANOTHER_CHANGEPOINT_CANCELLED');
              setIsConfirmingAnother(false);
            },
            confirmText: FEEDBACK_OPTION_COPY.add_changepoint.title,
            onConfirmButtonClick: () => {
              track('RESULT_FEEDBACK_ANOTHER_CHANGEPOINT_CONFIRMED');
              submitChangepoint();
            },
          };
        }
        return {
          ...backTo('expected_change'),
          confirmText: FEEDBACK_OPTION_COPY.add_changepoint.title,
          confirmDisabled: !isPickedPointValid,
          onConfirmButtonClick: handleAddChangepointClick,
        };
      case 'override_confirm':
        return {
          cancelText: 'Cancel',
          onCancelButtonClick: () => {
            track('RESULT_FEEDBACK_OVERRIDE_CANCELLED', {
              feedback_type: 'EXPECTED_CHANGE_POINT',
            });
            goToStep('add_changepoint');
          },
          confirmText: 'Replace',
          onConfirmButtonClick: () => {
            track('RESULT_FEEDBACK_OVERRIDE_REPLACED', {
              feedback_type: 'EXPECTED_CHANGE_POINT',
            });
            submitChangepoint(true);
          },
        };
    }
  })();

  return (
    <Dialog
      open
      onClose={handleClose}
      title={RESULT_FEEDBACK_DIALOG_TITLE}
      customWidth={step === 'add_changepoint' ? 900 : 540}
      {...footerProps}
    >
      <FlexColumn sx={{ gap: 2 }}>
        {step === 'options' && (
          <>
            <Typography>{OPTIONS_STEP_TITLE}</Typography>
            {OPTIONS_STEP_OPTIONS.filter(isOptionVisible).map((option) => (
              <FeedbackOptionCard
                key={option}
                option={option}
                onClick={handleOptionClick}
              />
            ))}
          </>
        )}

        {step === 'expected_change' && (
          <>
            <Typography>{EXPECTED_CHANGE_STEP_TITLE}</Typography>
            {EXPECTED_CHANGE_STEP_OPTIONS.filter(isOptionVisible).map(
              (option) => (
                <FeedbackOptionCard
                  key={option}
                  option={option}
                  onClick={handleOptionClick}
                />
              ),
            )}
          </>
        )}

        {step === 'real_issue' && <Typography>{REAL_ISSUE_MESSAGE}</Typography>}

        {step === 'add_context' && (
          <FormField title={CONTEXT_FIELD_TITLE} description={CONTEXT_NOTE}>
            <TextField
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder={CONTEXT_PLACEHOLDER}
              variant="outlined"
              multiline
              rows={3}
              inputProps={{ 'aria-label': CONTEXT_FIELD_TITLE }}
            />
          </FormField>
        )}

        {step === 'add_changepoint' && (
          <FlexColumn sx={{ gap: 1 }}>
            <AnomalyChart
              headerTitle={CHANGEPOINT_PROMPT}
              changepointMarkers={changepointMarkers}
              metrics={result.chart.metrics}
              metricsTitle={result.chart.metricsTitle}
              failOnZero={result.chart.failOnZero}
              anomalyDirection={result.chart.anomalyDirection}
              detectionPeriod={result.chart.detectionPeriod}
              detectionDelay={result.chart.detectionDelay}
              status={result.status}
              onPointClick={handlePointClick}
              selectedPointBucketStart={pickedBucketStart}
              disabledPointBucketStarts={disabledBucketStarts}
            />
            {pickedPoint &&
              (isConfirmingAnother ? (
                <Alert severity="warning">
                  {getAnotherChangepointMessage(
                    otherChangepoints.length,
                    pickedPoint.bucket_start,
                  )}
                </Alert>
              ) : (
                <Typography>{formatSelectedPoint(pickedPoint)}</Typography>
              ))}
          </FlexColumn>
        )}

        {step === 'override_confirm' && pickedPointChangepoint && (
          <Typography>
            {getReplaceChangepointMessage(
              pickedPointChangepoint.createdBy,
              pickedPointChangepoint.createdAt,
            )}
          </Typography>
        )}
      </FlexColumn>
    </Dialog>
  );
};

export default ResultFeedbackDialog;
